import { useCallback, useEffect, useState } from "preact/hooks";
import type { Article, SortKey } from "../lib/types";
import { listArticles, archiveArticle, favoriteArticle, deleteArticles } from "../lib/db";
import { navigate } from "../lib/router";
import { ArticleList } from "../components/ArticleList";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "saved", label: "Saved" },
  { key: "published", label: "Published" },
  { key: "title", label: "Title" },
];

export function Archive() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("saved");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setArticles(await listArticles({ filter: "archive", sort, search: search || undefined }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sort, search]);

  useEffect(() => { void reload(); }, [reload]);

  const onUnarchive = async (id: string) => {
    await archiveArticle(id, false);
    setArticles((prev) => prev.filter((a) => a.id !== id));
  };

  const onFavorite = async (id: string, value: boolean) => {
    await favoriteArticle(id, value);
    setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, favorite: value } : a)));
  };

  const onBulkDelete = async () => {
    if (!selected.size) return;
    if (!confirm(`Delete ${selected.size} article(s)? This cannot be undone.`)) return;
    await deleteArticles([...selected]);
    setSelected(new Set());
    await reload();
  };

  return (
    <>
      <div class="topbar">
        <h1>Archive</h1>
        <div class="grow" />
        <select value={sort} onChange={(e) => setSort((e.target as HTMLSelectElement).value as SortKey)}
          aria-label="Sort by">
          {SORTS.map((s) => <option value={s.key}>{s.label}</option>)}
        </select>
        <input type="search" placeholder="Search archive…" value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          aria-label="Search archive" style="min-width: 12rem;" />
      </div>

      {selected.size > 0 && (
        <div class="banner" style="display: flex; gap: var(--space-3); align-items: center;">
          <strong>{selected.size} selected</strong>
          <div class="grow" />
          <button onClick={onBulkDelete} style="color: var(--danger);">Delete</button>
          <button class="ghost" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {error && <div class="banner error">{error}</div>}

      {loading ? (
        <div class="empty"><div class="spinner" /></div>
      ) : articles.length === 0 ? (
        <div class="empty">
          <h2>Archive is empty</h2>
          <p class="muted">Archived articles appear here.</p>
        </div>
      ) : (
        <ArticleList
          articles={articles}
          selected={selected}
          onToggleSelect={(id) => setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          onArchive={onUnarchive}
          onFavorite={onFavorite}
          onOpen={(a) => navigate(`/articles/${a.id}`)}
        />
      )}
    </>
  );
}
